interface MarkdownNode {
	children?: MarkdownNode[]
	position?: { end: { offset?: number }; start: { offset?: number } }
	type: string
	value?: string
}

const nonBreakingSpace = "\u00a0"

const countNewlines = (text: string) => (text.replace(/\r\n/g, "\n").match(/\n/g) ?? []).length

// Same rule as the markdown of the editor: every pair of line breaks between two blocks separates them, each pair
// beyond that is an empty paragraph. Before the first and after the last block every pair is one.
const toEmptyParagraphCount = (newlines: number, isBoundary: boolean) =>
	Math.max(Math.floor(newlines / 2) - (isBoundary ? 0 : 1), 0)

const createEmptyParagraphs = (count: number): MarkdownNode[] =>
	Array.from({ length: count }, () => ({ children: [{ type: "text", value: nonBreakingSpace }], type: "paragraph" }))

/**
 * The editor writes the first of several empty lines as blank lines, which markdown ignores, and every further one as
 * "&nbsp;". This turns the blank lines into empty paragraphs, so that the preview shows as many empty lines as the editor.
 * It has to run before the plugins that replace nodes, as it needs their positions.
 */
export const remarkEmptyParagraphs = () => (tree: MarkdownNode, file: { value: unknown }) => {
	const source = String(file.value)
	const blocks = tree.children ?? []

	tree.children = blocks.flatMap((block, index) => {
		const previousEnd = index === 0 ? 0 : blocks[index - 1].position?.end.offset
		const start = block.position?.start.offset
		const isLast = index === blocks.length - 1
		const end = block.position?.end.offset

		const before =
			previousEnd !== undefined && start !== undefined
				? createEmptyParagraphs(
						toEmptyParagraphCount(countNewlines(source.slice(previousEnd, start)), index === 0),
					)
				: []
		const after =
			isLast && end !== undefined
				? createEmptyParagraphs(toEmptyParagraphCount(countNewlines(source.slice(end)), true))
				: []

		return [...before, block, ...after]
	})
}
